import { type Observable, skip } from 'rxjs'
import { type Member } from '@/features/iam/members/Member'
import { type Team } from '@/features/iam/teams/Team'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type {
  GetCurrentMemberFn,
  GetCurrentUserFn,
  GetTeamMemberFn,
  PrimeWorkspaceFn,
} from '@/features/iam'
import { collection } from '@/infra/collection'
import { clearMemoized } from '@/utils/memoize-util'
import { memoizeAsync } from '@/utils/memoize-decorator'
import { Id } from '@/infra/id'
import { maybeParseDate, unixEpoch } from '@/utils/date-util'
import { type BillingApi } from '@/features/billing/api/billing-api'
import { type CreateBillingMethodRequest } from '@/features/billing/api/resources/billing-method'
import { type BillingProfileResource } from '@/features/billing/api/resources/billing-profile'
import { type BillingMethod } from './BillingMethod'
import { mapBillingMethodResourceToProps } from './billing-method-mappers'
import { BillingProfile, type BillingProfileProps } from './BillingProfile'
import { type SetupIntent } from './SetupIntent'
import { mapSetupIntentResourceToSetupIntent } from './setup-intent-mappers'

/**
 * Manages billing profiles and their billing methods.
 */
export class BillingProfileStore {
  /**
   * Billing profiles.
   */
  readonly billingProfiles = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new BillingProfile(),
  })

  constructor(
    private readonly _api: BillingApi,
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    private readonly _getTeamMember: GetTeamMemberFn,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    currentMember$: Observable<Member>,
  ) {
    // Constructed once billing has loaded, so the replayed member is the one
    // the cache is being filled for.
    currentMember$
      .pipe(skip(1))
      .subscribe(() => this._clearIndividualBillingProfileCache())
  }

  /**
   * Gets the loaded billing profile of the member, if any.
   */
  individualBillingProfile(member: Member) {
    return (
      this.billingProfiles.find(
        (p) => p.workspace === member.workspace && p.userId === member.user.id,
      ) ?? null
    )
  }

  /**
   * Gets the loaded billing methods of the member.
   */
  billingMethodsForIndividual(member: Member) {
    const profile = this.individualBillingProfile(member)
    return profile ? profile.billingMethods.slice() : []
  }

  /**
   * Gets the loaded billing profile of the team, if any.
   */
  teamBillingProfile(team: Team) {
    return this.billingProfiles.find((x) => x.teamId === Id.assert(team.id))
  }

  /**
   * Gets the loaded billing methods of the team.
   */
  billingMethodsForTeam(team: Team) {
    const profile = this.teamBillingProfile(team)
    return profile ? profile.billingMethods.slice() : []
  }

  /**
   * Reactively determines whether the current user may manage the profile.
   */
  canManage(profile: BillingProfile) {
    const member = this._getCurrentMember(profile.workspace.id)
    if (member.permissions.adminBillingManage) {
      return true
    }

    if (profile.teamId) {
      return Boolean(
        this._getTeamMember(profile.teamId, member.user.publicId)?.permissions
          .manageTeamBilling,
      )
    }

    if (profile.userId) {
      return profile.userId === member.user.id
    }

    return false
  }

  /**
   * Fetches the billing profile of a workspace member.
   * NOTE: This is memoized.
   */
  @memoizeAsync({
    keySelector: ([workspaceId, userPublicId]) =>
      `${workspaceId}/${userPublicId}`,
  })
  async fetchBillingProfileForIndividual(
    workspaceId: Id,
    userPublicId: Id,
  ): Promise<BillingProfile> {
    const data = await this._api.profiles.getForMember(
      Id.assert(workspaceId),
      Id.assert(userPublicId),
    )
    return this.billingProfiles.set(
      this._mapBillingProfileResourceToProps(data),
    )
  }

  /**
   * Fetches the billing profile of a team.
   * NOTE: This is memoized.
   */
  @memoizeAsync()
  async fetchBillingProfileForTeam(teamId: Id): Promise<BillingProfile> {
    const data = await this._api.profiles.getForTeam(Id.assert(teamId))
    return this.billingProfiles.set(
      this._mapBillingProfileResourceToProps(data),
    )
  }

  /**
   * Fetches the billing methods of a team.
   */
  async fetchBillingMethodsForTeam(team: Team) {
    const profile = await this.fetchBillingProfileForTeam(Id.assert(team.id))
    return profile.billingMethods.slice()
  }

  /**
   * Fetches the billing methods of a workspace member.
   */
  async fetchBillingMethodsForIndividual(member: Member) {
    const profile = await this.fetchBillingProfileForIndividual(
      Id.assert(member.workspace.id),
      Id.assert(member.user.publicId),
    )
    return profile.billingMethods.slice()
  }

  /**
   * Makes the billing method the default of the current user's profile, or of
   * the team's when given.
   */
  async changeDefaultBillingMethod(method: BillingMethod, team?: Team | null) {
    const payload = {
      default_billing_method_id: Id.assert(method.id),
    }
    const data = await (team
      ? this._api.profiles.updateForTeam(Id.assert(team.id), payload)
      : this._api.profiles.updateForMember(
          Id.assert(method.workspaceId),
          Id.assert(this._getCurrentUser().publicId),
          payload,
        ))

    return this.billingProfiles.set(
      this._mapBillingProfileResourceToProps(data),
    )
  }

  /**
   * Adds a card billing method from a Stripe token.
   */
  async addBillingMethodForStripeToken(
    member: Member,
    team: Team | undefined | null,
    token: string,
  ): Promise<BillingMethod> {
    const profile = team
      ? await this.fetchBillingProfileForTeam(Id.assert(team.id))
      : await this.fetchBillingProfileForIndividual(
          member.workspace.id,
          member.user.publicId,
        )

    const payload: CreateBillingMethodRequest = {
      type: 'Stripe',
      user_id: profile.userPublicId,
      workspace_id: Id.assert(profile.workspace.id),
      token,
      team_id: team ? team.id : undefined,
    }
    const data = await this._api.methods.create(payload)
    return profile.billingMethods.set(mapBillingMethodResourceToProps(data))
  }

  /**
   * Deletes the billing method, unless it is the default.
   */
  async deleteBillingMethod(method: BillingMethod) {
    if (method.isDefault) {
      return
    }
    await this._api.methods.delete(Id.assert(method.id))
    method.billingProfile.billingMethods.remove(Id.assert(method.id))
  }

  /**
   * Sets up a Stripe Payment Method Intent for the member or team.
   */
  async setupStripePaymentMethodIntent(
    member: Member,
    team: Team | undefined | null,
  ): Promise<SetupIntent> {
    const workspaceId = team ? team.workspace.id : member.workspace.id

    const data = await this._api.methods.createStripeSetupIntent({
      workspace_id: Id.assert(workspaceId),
      user_id: member.user.publicId,
      team_id: team ? Id.assert(team.id) : null,
    })
    return mapSetupIntentResourceToSetupIntent(data)
  }

  /**
   * Completes the setup of a Stripe Payment Method Intent.
   */
  async completeSetupStripePaymentMethodIntent(
    setupIntentId: string,
    member: Member,
    team: Team | undefined | null,
  ): Promise<BillingMethod> {
    const profile = team
      ? await this.fetchBillingProfileForTeam(Id.assert(team.id))
      : await this.fetchBillingProfileForIndividual(
          member.workspace.id,
          member.user.publicId,
        )

    const data =
      await this._api.methods.completeStripeSetupIntent(setupIntentId)
    return profile.billingMethods.set(mapBillingMethodResourceToProps(data))
  }

  private _clearIndividualBillingProfileCache() {
    clearMemoized(this.fetchBillingProfileForIndividual)
  }

  private _mapBillingProfileResourceToProps(
    resource: BillingProfileResource,
  ): BillingProfileProps {
    return {
      id: resource.id,
      workspace:
        this._primeWorkspace({ id: resource.workspace_id }) ?? Workspace.none,
      defaultBillingMethodId: resource.default_billing_method_id,
      defaultPayoutMethodId: resource.default_payout_method_id,
      dateCreated: maybeParseDate(resource.date_created) ?? unixEpoch(),
      dateModified: maybeParseDate(resource.date_modified) ?? unixEpoch(),
      userId: resource.user_id,
      userPublicId: resource.user_public_id,
      referralCouponCode: resource.referral_coupon_code,
      referralCouponId: resource.referral_coupon_id,
      teamId: resource.team_id,
      billingMethods: resource.billing_methods.map(
        mapBillingMethodResourceToProps,
      ),
    }
  }
}
